import { Button, Illustration, Typography } from "@saviynt/design-system";
import { FormattedMessage } from "react-intl";
import { useTheme } from "@repo/mfe-core";

export type EmptyStateVariant = "empty" | "no-results";

interface IntegrationsEmptyStateProps {
  variant?: EmptyStateVariant;
  onGetStarted?: () => void;
}

export function IntegrationsEmptyState({
  variant = "empty",
  onGetStarted,
}: IntegrationsEmptyStateProps) {
  const { theme } = useTheme();

  if (variant === "no-results") {
    return (
      <div className="Integrations-emptyState flex items-center justify-center flex-1 min-h-0 overflow-auto">
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <Illustration kind="Empty" />
          <Typography kind="h2" className="mb-2">
            <FormattedMessage
              id="8Id8zt"
              defaultMessage="No results found"
              description="Message when filters return no results"
            />
          </Typography>
          <Typography
            kind="body2"
            dataTheme={theme === "dark" ? "dark" : "light"}
          >
            <FormattedMessage
              id="rG93S3"
              defaultMessage="Try adjusting your filters to see more results"
              description="Description when filters return no results"
            />
          </Typography>
        </div>
      </div>
    );
  }

  // Default: "empty" variant - Show "No Integrations Yet"
  return (
    <div className="Integrations-emptyState flex flex-col items-center justify-center flex-1 py-16">
      <div className="mb-8">
        <Illustration kind="Confirmation" />
      </div>

      {/* Title */}
      <div className="mb-4">
        <Typography kind="h1" className="mb-4">
          <FormattedMessage
            id="yGXZ/N"
            defaultMessage="No Integrations Yet"
            description="Empty state title when no integrations exist"
          />
        </Typography>
      </div>
      {/* Description */}
      <Typography kind="body2" className="text-center mb-8 px-4">
        <FormattedMessage
          id="Ix1KAZ"
          defaultMessage="Connect your discovery sources, applications, and platforms to bring their data into{lineBreak}the platform for easy visibility and management."
          description="Empty state description explaining what integrations do"
          values={{
            lineBreak: <br />,
          }}
        />
      </Typography>

      {/* Get Started Button */}
      {onGetStarted && (
        <div className="py-4">
          <Button kind="filled" onClick={onGetStarted}>
            <FormattedMessage
              id="MGd79q"
              defaultMessage="Get Started"
              description="Button to start adding integrations"
            />
          </Button>
        </div>
      )}
    </div>
  );
}
